import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";

const EMAIL = "auralia-healing@gmx.de";
const PHONE = "+49 155 6724186";
const WHATSAPP = "491556724186";

export default function Kontakt() {
  return (
    <div className="bg-[#050510] text-[#F0EDE8] min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Kontakt
          </p>
          <h1
            className="text-5xl md:text-6xl font-light text-[#F0EDE8] mb-6"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Ich freue mich auf dich
          </h1>
          <p className="text-[#9A9A9A]">
            Du erhältst eine Antwort innerhalb von 24 Stunden.
          </p>
        </motion.div>

        {/* Contact cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <a
            href={`mailto:${EMAIL}`}
            className="flex items-center gap-5 bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-7 hover:border-[#C9A84C]/40 transition-all duration-300 group"
            data-testid="contact-email-link"
          >
            <div className="w-12 h-12 rounded-full border border-[#1A1A2E] group-hover:border-[#C9A84C]/40 flex items-center justify-center text-[#C9A84C]">
              <Mail size={20} />
            </div>
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A9A9A] mb-1">E-Mail</p>
              <p className="text-[#F0EDE8] text-sm">{EMAIL}</p>
            </div>
          </a>
          <a
            href={`https://wa.me/${WHATSAPP}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-5 bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-7 hover:border-[#C9A84C]/40 transition-all duration-300 group"
            data-testid="contact-whatsapp-link"
          >
            <div className="w-12 h-12 rounded-full border border-[#1A1A2E] group-hover:border-[#C9A84C]/40 flex items-center justify-center text-[#C9A84C]">
              <Phone size={20} />
            </div>
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A9A9A] mb-1">
                Telefon / WhatsApp
              </p>
              <p className="text-[#F0EDE8] text-sm">{PHONE}</p>
            </div>
          </a>
        </motion.div>

        {/* Contact form */}
        <KontaktForm />
      </div>
    </div>
  );
}

function KontaktForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "", privacy: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) { setError("Bitte akzeptiere die Datenschutzerklärung."); return; }
    setLoading(true);
    setError("");
    try {
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      const { default: axios } = await import("axios");
      await axios.post(`${API}/contacts`, { name: form.name, email: form.email, message: form.message });
      setSuccess(true);
    } catch {
      setError("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#1A1A2E] text-[#F0EDE8] focus:border-[#C9A84C] focus:outline-none py-4 text-sm placeholder-[#9A9A9A]/40 transition-colors duration-300";

  return (
    <motion.div
      className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-10"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {success ? (
        <motion.div
          className="text-center py-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="text-5xl text-[#C9A84C] mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</div>
          <h3 className="text-3xl font-light text-[#F0EDE8] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Vielen Dank!
          </h3>
          <p className="text-[#9A9A9A] text-sm">
            Deine Nachricht wurde erfolgreich gesendet. Ich melde mich innerhalb von 24 Stunden bei dir.
          </p>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2 block">Name</label>
            <input type="text" required value={form.name} onChange={set("name")}
              placeholder="Dein vollständiger Name" className={inputClass} data-testid="contact-name-input" />
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2 block">E-Mail</label>
            <input type="email" required value={form.email} onChange={set("email")}
              placeholder="deine@email.de" className={inputClass} data-testid="contact-email-input" />
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2 block">Nachricht</label>
            <textarea required value={form.message} onChange={set("message")}
              placeholder="Wie kann ich dir helfen?" rows={5}
              className={`${inputClass} resize-none`} data-testid="contact-message-input" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer group">
            <button type="button" role="checkbox" aria-checked={form.privacy}
              onClick={() => setForm((f) => ({ ...f, privacy: !f.privacy }))}
              className={`w-4 h-4 mt-0.5 flex-shrink-0 border flex items-center justify-center transition-all ${
                form.privacy ? "border-[#C9A84C] bg-[#C9A84C]/20" : "border-[#1A1A2E] group-hover:border-[#C9A84C]/50"
              }`} data-testid="contact-privacy-checkbox">
              {form.privacy && <div className="w-2 h-2 bg-[#C9A84C]" />}
            </button>
            <span className="text-xs text-[#9A9A9A] leading-relaxed">
              Ich habe die <span className="text-[#C9A84C]">Datenschutzerklärung</span> gelesen und stimme zu.
            </span>
          </label>
          {error && <p className="text-red-400 text-xs" data-testid="contact-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300 disabled:opacity-40"
            data-testid="contact-submit-btn">
            {loading ? "Wird gesendet ..." : "Nachricht senden"}
          </button>
        </form>
      )}
    </motion.div>
  );
}

import { useState } from "react";
